import type {
    ProviderGroup,
    SavePenPatch,
    SaveProviderConfigInput,
    TestPenInput,
    TestProviderConfigInput,
} from '@/domain/providerConfig';

import type { FormDiff, PenDiff } from './formDiff';

function penPatch(cageId: number, pen: PenDiff, group: ProviderGroup | null): SavePenPatch {
    const row = pen.create ? undefined : group?.rows.find((r) => r.cageId === cageId);
    return {
        cageId,
        rowId: row?.rowId ?? null,
        expectedUpdatedAt: row?.updatedAt ?? null,
        ...(pen.isActive !== undefined && { isActive: pen.isActive }),
        ...(pen.remove && { remove: true }),
        fields: { ...pen.fields },
        secrets: { ...pen.secrets },
    };
}

/** A site change writes every row, so the server wants each one listed with its version. */
function fansOut(diff: FormDiff): boolean {
    return Object.keys(diff.connection).length > 0 || Object.keys(diff.secrets).length > 0;
}

function penEntries(diff: FormDiff): [number, PenDiff][] {
    return Object.entries(diff.pens).map(([cageId, pen]): [number, PenDiff] => [
        Number(cageId),
        pen,
    ]);
}

/**
 * The save request for a diff. Each changed pen names its row and the version the form
 * was built from; a site change adds every other row as an unchanged reference.
 */
export function buildSaveInput(
    diff: FormDiff,
    group: ProviderGroup | null,
    extras: { testToken?: string; testOverride?: boolean }
): SaveProviderConfigInput {
    const pens = penEntries(diff).map(([cageId, pen]) => penPatch(cageId, pen, group));
    if (fansOut(diff)) {
        const listed = new Set(pens.map((pen) => pen.cageId));
        for (const row of group?.rows ?? [])
            if (!listed.has(row.cageId))
                pens.push({
                    cageId: row.cageId,
                    rowId: row.rowId,
                    expectedUpdatedAt: row.updatedAt,
                    fields: {},
                    secrets: {},
                });
    }
    pens.sort((a, b) => a.cageId - b.cageId);
    return {
        connection: { ...diff.connection },
        secrets: { ...diff.secrets },
        pens,
        ...(extras.testToken !== undefined && { testToken: extras.testToken }),
        ...(extras.testOverride !== undefined && { testOverride: extras.testOverride }),
    };
}

/**
 * The test request a save of `diff` needs signed (R2, R16). Connection and secrets are the
 * diff's own, and each pen carries the same values the save sends it, so the server's
 * digest of the test matches its digest of the save. Beyond the pens chosen, every pen
 * with its own typed or cleared secret or its own connection value is tested, since the
 * token covers them too. Pens being removed are not tested.
 */
export function buildTestInput(diff: FormDiff, cageIds: number[]): TestProviderConfigInput {
    const tested = new Set(cageIds);
    for (const [cageId, pen] of penEntries(diff))
        if (Object.keys(pen.secrets).length > 0 || Object.keys(pen.connectionChanges).length > 0)
            tested.add(cageId);
    const pens = [...tested]
        .filter((cageId) => !diff.pens[cageId]?.remove)
        .sort((a, b) => a - b)
        .map((cageId): TestPenInput => {
            const pen = diff.pens[cageId];
            return {
                cageId,
                fields: { ...(pen?.fields ?? {}) },
                secrets: { ...(pen?.secrets ?? {}) },
            };
        });
    return { connection: { ...diff.connection }, secrets: { ...diff.secrets }, pens };
}
